<template>
  <Wrap>
    <UiBreadcrumbs
      :list="crumbs"
      :events-identifier="''"
      :track-event="breadcrumbsTrackEvent"
    />
    <h2 class="title">Читатели делятся опытом путешествий с&nbsp;Трипстером</h2>
    <WriteBanner />

    <MosaicListView
      class="articles-mosaic-list"
      :items="articles"
      :more-button="hasMoreArticles"
      more-button-text="Показать ещё"
      more-data-test="destination-articles-btn-read-more"
      :loading="loading"
      no-last-item-margin
      :number-of-skeleton-cards="2"
      @load-more="fetchData"
    >
      <template #default="{ item, mode }">
        <AppLink
          class="article-card-wrapper"
          :to="`/journal/articles/${item.slug}/`"
        >
          <Card
            :item="item"
            :mode="mode"
            :locale="ru"
          />
        </AppLink>
      </template>
      <template #skeleton="{ mode }">
        <Skeleton
          :mode="mode"
          :loading="loading"
        />
      </template>
    </MosaicListView>
  </Wrap>

  <SubscriptionBlock
    :api-url="``"
    :analytics-api-url="VITE_RUNTIME_SOGU_SERVICE_URL"
    page-type="listing"
  />
</template>

<script lang="ts" setup>
  import { UiBreadcrumbs, type TrackEvent } from '@experience/core-breadcrumbs-ui';
  import { Card, Skeleton } from '@experience/mosaic-list-article-card-package';
  import { MosaicListView } from '@experience/mosaic-list-view-package';
  import { SubscriptionBlock } from '@experience/subscription-block-package';
  import { useHead } from '@unhead/vue';
  import { ru } from 'date-fns/locale';
  import { storeToRefs } from 'pinia';
  import { useRuntimeConfig } from 'virtual:runtime-config';
  import { onBeforeMount, onBeforeUnmount, ref } from 'vue';
  import { trackEvent } from '@/services/analytics';
  import { MF_NAME } from '@/app/const';
  // eslint-disable-next-line boundaries/element-types
  import WriteBanner from '@/pages/articlesOrSights/components/WriteBanner.vue';
  import { useJournalContent } from '@/entities/journalContent';
  import Wrap from '@/shared/ui/Wrap.vue';

  const { VITE_RUNTIME_SOGU_SERVICE_URL } = useRuntimeConfig()[MF_NAME];

  const breadcrumbsTrackEvent = trackEvent as TrackEvent;

  const pageSize = ref(10);
  const store = useJournalContent();
  const { getArticles, reset } = store;
  const { articles, loading, hasNext: hasMoreArticles } = storeToRefs(store);

  const fetchData = async () => {
    try {
      await getArticles({
        pageSize: pageSize.value,
        is_ugc: true,
      });
    } catch (error) {
      // Failed to load UGC articles
    }
  };

  const crumbs = [
    { label: 'Главная', to: '/' },
    { label: 'Журнал', to: '/journal/' },
  ];

  const title = 'Стать автором — Журнал Трипстера';
  const description =
    'Вдохновите других на открытия, расскажите о своём путешествии с Трипстером и мы опубликуем вашу статью в нашем Журнале.';

  useHead({
    title,
    link: [
      {
        rel: 'canonical',
        href: `${window.location.origin}${window.location.pathname}`,
      },
    ],
    meta: [
      {
        name: 'description',
        content: () => description,
      },
      {
        property: 'og:title',
        content: () => title,
      },
      {
        property: 'og:description',
        content: () => description,
      },
    ],
  });

  onBeforeMount(async () => {
    await fetchData();
  });

  onBeforeUnmount(() => {
    reset();
  });
</script>

<style lang="scss" scoped>
  .title {
    display: flex;
    gap: 0 var(--alias-space-8);
    align-items: center;
    max-width: 910px;
    margin-top: 32px;
    color: var(--alias-color-text-default);

    @include base-900-850-extra;

    @include tablet {
      gap: 0 var(--alias-space-4);
      margin-top: 24px;

      @include base-800-750-extra;
    }

    @include mobile {
      margin-top: 36px;
      margin-bottom: 0;
    }
  }

  .articles-mosaic-list {
    margin-top: 32px;

    @include mobile {
      margin-top: 24px;
    }
  }

  .article-card-wrapper {
    text-decoration: none;
  }
</style>
